import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import { BFormGroup, BRow, BCol, BInputGroup } from 'bootstrap-vue'
import { AxiosResponse } from 'axios'
import {
  KForm,
  KFormInput,
  KFormNumber,
  KFormDatetime,
  KFormSpinner,
  KFormEditor,
  KFormDatepicker,
  KFormSelect,
  KFormTextarea
} from '@kwixeo/form-vue'
import { CustomerSelect, ContactSelect } from '@kwixeo/customers-vue'
import { TeamUserSelect, emitter, Spinner } from '@kwixeo/interface-vue'
import { mapActions, mapGetters, mapState } from 'vuex'
import { CustomFieldsForm } from '@kwixeo/custom-fields-vue'
import { fetchOpportunity } from '../../fetchDatas/opportunity'

@Component({
  computed: {
    ...mapGetters('user', ['currency', 'teamConfigs']),
    ...mapGetters('opportunities', ['funnelStep', 'categories', 'availableFunnels']),
    ...mapState('opportunities', ['lossReasons']),
    ...mapGetters('subscriptions', ['hasModule'])
  },
  methods: {
    ...mapActions('opportunities', ['loadFunnels', 'loadLossReasons'])
  },
  components: {
    KForm, KFormInput, KFormNumber, KFormDatetime, KFormSpinner, KFormEditor, KFormDatepicker, KFormSelect, KFormTextarea,
    BFormGroup, BRow, BCol, BInputGroup,
    CustomerSelect, ContactSelect,
    TeamUserSelect, Spinner,
    CustomFieldsForm
  }
})
export default class OpportunityForm extends Vue {
  currency!: KwixeoFramework.Currency
  loadFunnels!: (force?: boolean) => Promise<void>
  funnelStep!: (funnelId: number) => KwixeoForm.SelectOptions
  categories!: (funnelId: number) => KwixeoOpportunities.FunnelCategories
  availableFunnels!: KwixeoOpportunities.Funnels
  loadLossReasons!: () => Promise<void>
  lossReasons!: KwixeoOpportunities.LossReasons
  hasModule!: (key: string) => boolean
  teamConfigs!: KwixeoFramework.ConfigAttributes

  @Prop({ default: null }) value!: KwixeoOpportunities.Opportunity|null
  @Prop({ default: false }) redirect!: boolean

  private opportunity: KwixeoOpportunities.OpportunityForm = {
    customer_child: null,
    step: null,
    custom_fields: []
  }

  private loading = false
  private funnelId: number|null = null
  private initialized = true

  async mounted (): Promise<void> {
    this.loading = true
    await this.loadLossReasons()
    await this.loadFunnels()
    if (this.availableFunnels.length === 1) {
      this.funnelId = this.availableFunnels[0].id
    }
    this.loading = false
  }

  get formAction (): string {
    let url = '/opportunity'
    if (this.opportunity.id) {
      url += '/' + this.opportunity.id
    }
    return url
  }

  get formMethod (): string {
    return this.opportunity.id !== undefined ? 'PUT' : 'POST'
  }

  @Watch('value', { immediate: true })
  private async watchValue (value: KwixeoOpportunities.Opportunity|null): Promise<void> {
    this.initialized = false

    if (value) {
      if (value.id) {
        this.loading = true
        value = await fetchOpportunity(value.id)
        this.loading = false
      }
      this.opportunity = Object.assign({
        custom_fields: [],
        category: null,
        step: null,
        customer_child: null
      },JSON.parse(JSON.stringify(value)))
      this.funnelId = value.funnel?.id ?? null
    }

    this.$nextTick(() => {
      this.initialized = true
    })
  }

  @Watch('opportunity.customer')
  private watchCustomer (value: KwixeoCustomers.Customer|null, old: KwixeoCustomers.Customer|null): void {
    if (!this.initialized) {
      return
    }

    if (value?.id !== old?.id) {
      this.opportunity.contact = null
      this.opportunity.customer_child = null
    }

    if (this.hasModule('customers-group') && this.teamConfigs.customer_group_opportunity_parent_select && value && value.parent) {
      this.opportunity.customer = value.parent
      this.$nextTick(() => {
        this.opportunity.customer_child = value
      })
      return
    }
  }

  private stepSelected (step: KwixeoOpportunities.FunnelStep|null): void {
    if (step && step.probability > 0) {
      this.opportunity.probability = step.probability
    }
  }

  private submitComplete (response: AxiosResponse): void {
    const opportunity = response.data
    if (this.opportunity.id) {
      emitter.emit('opportunityUpdated', { old: this.value as KwixeoOpportunities.Opportunity, new: opportunity })
    } else {
      emitter.emit('opportunityCreated', opportunity)
    }

    this.$emit('input', opportunity)

    if (this.redirect) {
      this.$router.push({
        name: 'opportunity-item',
        params: {
          id: String(opportunity.id)
        }
      })
    }
  }
}
